import {useOverviewCommits} from './use-overview-commits'
import {interleaveCommitGroups} from './utils/interleave-commits'
import type {CommentsSort} from './use-comments-sort-state'
import type {useFilteredConversationComments} from './use-filtered-conversation-comments'

export function useInterweaveCommentsWithCommits({
  includeCommits,
  filteredComments,
  conversationCommentsQuery,
  sort,
}: {
  includeCommits: boolean
  filteredComments: ReturnType<typeof useFilteredConversationComments>['filteredComments']
  conversationCommentsQuery: ReturnType<typeof useFilteredConversationComments>['query']
  sort: CommentsSort
}) {
  const commitsQuery = useOverviewCommits({enabled: includeCommits})

  if (!includeCommits) {
    return {
      commitsQuery,
      commentsAndCommits: interleaveCommitGroups(filteredComments, [], sort),
      totalCommitsCount: 0,
      showTruncationWarning: false,
      totalAvailableCommitsCount: 0,
    }
  }

  // Find the oldest comment so we do not show earlier commits
  const oldestLoadedCommentTimestamp =
    conversationCommentsQuery.data?.comments.reduce(
      (oldest, comment) => Math.min(oldest, Date.parse(comment.createdAt)),
      Number.POSITIVE_INFINITY,
    ) ?? Number.POSITIVE_INFINITY

  // reshape the commit groups to fit inbetween comments. Dynamically updates
  const allCommitGroups = commitsQuery.data?.commitGroups ?? []
  const commitGroups = conversationCommentsQuery.hasNextPage
    ? allCommitGroups.map(group => ({
        ...group,
        commits: group.commits.filter(
          commit => Date.parse(commit.committedDate ?? commit.authoredDate ?? '') >= oldestLoadedCommentTimestamp,
        ),
      }))
    : allCommitGroups

  // combine commits and comments into a giant ball of goo
  const commentsAndCommits = interleaveCommitGroups(filteredComments, commitGroups, sort)

  // Edge case: We can only fetch 250 commits, and only want to show a warning if there
  // the number of commits is high, and the number of comments is low.
  const totalCommitsCount = commitGroups.reduce((count, group) => count + group.commits.length, 0)
  const totalAvailableCommitsCount = allCommitGroups.reduce((count, group) => count + group.commits.length, 0)
  const showTruncationWarning =
    commitsQuery.data?.truncated && totalCommitsCount > 0 && totalCommitsCount === totalAvailableCommitsCount

  return {
    commitsQuery,
    commentsAndCommits,
    totalCommitsCount,
    showTruncationWarning,
    totalAvailableCommitsCount,
  }
}
